/**
 * WebSocket Test Page
 * Dedicated page for testing WebSocket subscriptions
 * - Public channels: orderbook, trades, ticker, last_price, funding_rate, deviation, index_price, kline, mark_price, price_feed, twap
 * - Private/Authenticated channels: user_updates (positions), buckets, balances, orders
 * - Multi-feed: Unified channel that streams all sub-channels (public and authenticated)
 */

import { useState, useEffect, useRef } from "react";
import { SubscriptionClient } from "../sdk";
import { useMarkets } from "../hooks/useMarkets";
import { useOnboard } from "../hooks/useOnboard";
import {
  DEFAULT_BASE_URL,
  DEFAULT_WS_URL,
  DEFAULT_DEVICE_ID,
} from "../configs/base";
import { SubscriptionButton } from "./websocket_test_page/WebSocketTestPageButtons";
import { ConnectionSteps, ConnectionStep } from "./websocket_test_page/ConnectionSteps";
import { ConfigSection } from "./websocket_test_page/ConfigSection";
import { ChannelSection } from "./websocket_test_page/ChannelSection";
import { Message } from "./websocket_test_page/MessageItem";
import { KlineChart, KlineChartRef, KlineUpdate } from "../components/finchartview";
import { CandlestickData, UTCTimestamp } from "lightweight-charts";
import { fromSatoshi } from "../sdk/utils/math";
import "./websocket_test_page/websockettest.css";

export default function WebSocketTestPage() {
  const [wsURL, setWsURL] = useState(DEFAULT_WS_URL);
  // Load authToken from localStorage on mount (shared with SDKTestPage)
  const [authToken, setAuthToken] = useState(() => {
    return localStorage.getItem("authToken") || "";
  });
  const [deviceID, setDeviceID] = useState(DEFAULT_DEVICE_ID);
  const [marketId, setMarketId] = useState<string>("");
  const [symbol, setSymbol] = useState<string>("BTC/USDC"); // For funding_rate, deviation, index_price, mark_price, price_feed, twap
  const [klineInterval, setKlineInterval] = useState<string>("1m"); // For kline subscription
  const [twapWindowSize, setTwapWindowSize] = useState<string>("300"); // For twap subscription (in seconds)
  const [isConnected, setIsConnected] = useState(false);
  const [publicMessages, setPublicMessages] = useState<Message[]>([]);
  const [privateMessages, setPrivateMessages] = useState<Message[]>([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState<Set<string>>(
    new Set(),
  );
  const [connectionSteps, setConnectionSteps] = useState<ConnectionStep[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);

  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const messageIdRef = useRef(0);
  const baseURL = DEFAULT_BASE_URL; // For fetching markets
  const klineChartRef = useRef<KlineChartRef | null>(null);
  const [klineBars, setKlineBars] = useState<CandlestickData[]>([]);

  // Use onboarding hook as single source of truth for account information
  const { walletAddress, authToken: onboardAuthToken } = useOnboard(baseURL);

  // Use markets hook to get market list
  const { markets } = useMarkets({
    baseURL,
    sessionToken: "",
    autoFetchMarkets: true,
  });

  // Compute default market ID
  const defaultMarketId =
    markets.length > 0
      ? (markets.find((m) => m.active)?.market_id ?? markets[0].market_id) ||
        (markets.find((m) => m.active)?.id?.toString() ??
          markets[0].id?.toString() ??
          "")
      : null;

  useEffect(() => {
    if (defaultMarketId && !marketId) {
      setMarketId(defaultMarketId);
    }
  }, [defaultMarketId]);

  // Sync onboard auth token to local state when it changes
  useEffect(() => {
    if (onboardAuthToken) {
      setAuthToken(onboardAuthToken);
    }
  }, [onboardAuthToken]);

  // Listen for authToken changes from localStorage (when SDKTestPage updates it)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "authToken" && e.newValue) {
        setAuthToken(e.newValue);
      }
    };

    // Listen for storage events (when other tabs/pages update localStorage)
    window.addEventListener("storage", handleStorageChange);

    // Also check localStorage periodically in case storage event doesn't fire
    // (e.g., when updated from same tab)
    const interval = setInterval(() => {
      const storedAuthToken = localStorage.getItem("authToken");
      if (storedAuthToken && storedAuthToken !== authToken) {
        setAuthToken(storedAuthToken);
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [authToken]);

  // Sync authToken to localStorage when manually changed
  useEffect(() => {
    if (authToken && authToken.trim() !== "") {
      localStorage.setItem("authToken", authToken);
    }
  }, [authToken]);

  const addMessage = (
    channel: string,
    data: any,
    type: "success" | "error" | "info" | "warning" | "debug",
    isPrivate: boolean,
    step?: string,
  ) => {
    const message: Message = {
      id: messageIdRef.current++,
      timestamp: Date.now(),
      channel,
      data,
      type,
      step,
    };

    if (isPrivate) {
      setPrivateMessages((prev) => [...prev.slice(-99), message]); // Keep last 100
    } else {
      setPublicMessages((prev) => [...prev.slice(-99), message]); // Keep last 100
    }
  };

  const addConnectionStep = (
    step: string,
    status: "pending" | "in-progress" | "success" | "error",
    message: string,
    details?: any,
  ) => {
    setConnectionSteps((prev) => [
      ...prev,
      {
        step,
        status,
        message,
        timestamp: Date.now(),
        details,
      },
    ]);
  };

  const clearConnectionSteps = () => {
    setConnectionSteps([]);
  };

  const connect = async () => {
    if (subscriptionClientRef.current?.isConnected()) {
      addMessage("connection", "Already connected", "info", false);
      return;
    }

    setIsConnecting(true);
    clearConnectionSteps();

    // Step 1: Validate configuration
    addConnectionStep("1", "in-progress", "Validating configuration...");
    addMessage(
      "connection",
      "Starting connection process...",
      "debug",
      false,
      "Step 1: Validation",
    );

    if (!wsURL || !wsURL.trim()) {
      addConnectionStep("1", "error", "WebSocket URL is required");
      addMessage(
        "connection",
        "Error: WebSocket URL is required",
        "error",
        false,
        "Step 1: Validation",
      );
      setIsConnecting(false);
      return;
    }

    // Validate URL format
    try {
      const url = new URL(wsURL);
      if (url.protocol !== "ws:" && url.protocol !== "wss:") {
        throw new Error("URL must use ws:// or wss:// protocol");
      }
      addConnectionStep("1", "success", `URL format valid: ${wsURL}`);
      addMessage(
        "connection",
        `URL validated: ${wsURL}`,
        "debug",
        false,
        "Step 1: Validation",
      );
    } catch (error: any) {
      addConnectionStep("1", "error", `Invalid URL format: ${error.message}`);
      addMessage(
        "connection",
        `Error: Invalid URL format - ${error.message}`,
        "error",
        false,
        "Step 1: Validation",
      );
      setIsConnecting(false);
      return;
    }

    // Step 2: Build connection URL with parameters
    addConnectionStep(
      "2",
      "in-progress",
      "Building connection URL with parameters...",
    );
    const params: string[] = [];
    if (authToken) params.push(`token=${encodeURIComponent(authToken)}`);
    if (walletAddress)
      params.push(`wallet=${encodeURIComponent(walletAddress)}`);
    if (deviceID) params.push(`device_id=${encodeURIComponent(deviceID)}`);

    const separator = wsURL.includes("?") ? "&" : "?";
    const fullURL =
      params.length > 0 ? `${wsURL}${separator}${params.join("&")}` : wsURL;

    addConnectionStep("2", "success", `Connection URL: ${fullURL}`);
    addMessage(
      "connection",
      `Connection URL: ${fullURL}`,
      "debug",
      false,
      "Step 2: URL Construction",
    );
    if (params.length > 0) {
      addMessage(
        "connection",
        `Query parameters: ${params.join(", ")}`,
        "debug",
        false,
        "Step 2: URL Construction",
      );
    } else {
      addMessage(
        "connection",
        "No query parameters (authToken, walletAddress, deviceID)",
        "warning",
        false,
        "Step 2: URL Construction",
      );
    }

    // Step 3: Cleanup existing connection
    addConnectionStep("3", "in-progress", "Cleaning up existing connection...");
    if (subscriptionClientRef.current) {
      try {
        subscriptionClientRef.current.disconnect();
        addConnectionStep("3", "success", "Previous connection cleaned up");
        addMessage(
          "connection",
          "Previous connection cleaned up",
          "debug",
          false,
          "Step 3: Cleanup",
        );
      } catch (error: any) {
        addConnectionStep("3", "success", "No previous connection to clean up");
        addMessage(
          "connection",
          "No previous connection found",
          "debug",
          false,
          "Step 3: Cleanup",
        );
      }
    } else {
      addConnectionStep("3", "success", "No previous connection to clean up");
    }

    // Step 4: Create SubscriptionClient
    addConnectionStep(
      "4",
      "in-progress",
      "Creating SubscriptionClient instance...",
    );
    try {
      subscriptionClientRef.current = new SubscriptionClient({
        url: wsURL,
        authToken: authToken || undefined,
        walletAddress: walletAddress || undefined,
        deviceID: deviceID || undefined,
      });
      addConnectionStep("4", "success", "SubscriptionClient created");
      addMessage(
        "connection",
        "SubscriptionClient instance created",
        "debug",
        false,
        "Step 4: Client Creation",
      );
      addMessage(
        "connection",
        `Config: ${JSON.stringify({ url: wsURL, hasToken: !!authToken, hasWallet: !!walletAddress, deviceID }, null, 2)}`,
        "debug",
        false,
        "Step 4: Client Creation",
      );
    } catch (error: any) {
      addConnectionStep(
        "4",
        "error",
        `Failed to create client: ${error.message}`,
      );
      addMessage(
        "connection",
        `Error creating client: ${error.message}`,
        "error",
        false,
        "Step 4: Client Creation",
      );
      setIsConnecting(false);
      return;
    }

    // Step 5: Attempt WebSocket connection
    addConnectionStep("5", "in-progress", "Initiating WebSocket connection...");
    addMessage(
      "connection",
      "Initiating WebSocket handshake...",
      "debug",
      false,
      "Step 5: Connection",
    );
    addMessage(
      "connection",
      "Browser will attempt to upgrade HTTP connection to WebSocket",
      "debug",
      false,
      "Step 5: Connection",
    );

    const connectionStartTime = Date.now();
    try {
      await subscriptionClientRef.current.connect();
      const connectionTime = Date.now() - connectionStartTime;

      const connected = subscriptionClientRef.current.isConnected();
      const state = subscriptionClientRef.current.getState();

      addMessage(
        "connection",
        `Connection attempt completed in ${connectionTime}ms`,
        "debug",
        false,
        "Step 5: Connection",
      );
      addMessage(
        "connection",
        `Connection state: ${state}`,
        "debug",
        false,
        "Step 5: Connection",
      );

      setIsConnected(connected);

      if (connected) {
        addConnectionStep(
          "5",
          "success",
          `WebSocket connected successfully (${connectionTime}ms)`,
        );
        addMessage(
          "connection",
          "✅ WebSocket connected successfully!",
          "success",
          false,
          "Step 5: Connection",
        );
        addMessage(
          "connection",
          `Connection established in ${connectionTime}ms`,
          "success",
          false,
          "Step 5: Connection",
        );
      } else {
        addConnectionStep(
          "5",
          "error",
          "Connection completed but state is not connected",
        );
        addMessage(
          "connection",
          "❌ Connection attempt completed but state is not connected",
          "error",
          false,
          "Step 5: Connection",
        );
        addMessage(
          "connection",
          `Current state: ${state}`,
          "error",
          false,
          "Step 5: Connection",
        );
      }
    } catch (error: any) {
      const connectionTime = Date.now() - connectionStartTime;

      // Parse error message for details
      const errorMessage = error.message || String(error);
      let errorDetails = "";

      if (errorMessage.includes("abnormal closure")) {
        errorDetails =
          "The server closed the connection abnormally. Possible causes:\n" +
          "- Server is not running\n" +
          "- Wrong URL or port\n" +
          "- Network connectivity issues\n" +
          "- Firewall blocking the connection";
      } else if (errorMessage.includes("protocol error")) {
        errorDetails =
          "WebSocket protocol error. The server may not support WebSocket connections.";
      } else if (errorMessage.includes("policy violation")) {
        errorDetails =
          "Connection closed due to policy violation. Check authentication requirements.";
      } else if (errorMessage.includes("server error")) {
        errorDetails =
          "Server returned an error. Check server logs for details.";
      } else if (
        errorMessage.includes("Unauthorized") ||
        errorMessage.includes("401")
      ) {
        errorDetails =
          "Authentication failed. Possible causes:\n" +
          "- Missing or invalid JWT token (if server requires authentication)\n" +
          "- Token expired\n" +
          "- Server is in production mode requiring JWT authentication\n" +
          "Note: If server uses JWT auth, you must provide a valid token in the authToken field.";
      }

      addConnectionStep("5", "error", `Connection failed: ${errorMessage}`);
      addMessage(
        "connection",
        `❌ Connection error: ${errorMessage}`,
        "error",
        false,
        "Step 5: Connection",
      );
      if (errorDetails) {
        addMessage(
          "connection",
          `Error details:\n${errorDetails}`,
          "error",
          false,
          "Step 5: Connection",
        );
      }
      addMessage(
        "connection",
        `Connection failed after ${connectionTime}ms`,
        "error",
        false,
        "Step 5: Connection",
      );
      addMessage(
        "connection",
        `Full error: ${JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}`,
        "error",
        false,
        "Step 5: Connection",
      );
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = () => {
    if (subscriptionClientRef.current) {
      try {
        subscriptionClientRef.current.disconnect();
        setIsConnected(false);
        setActiveSubscriptions(new Set());
        addMessage("connection", "WebSocket disconnected", "info", false);
      } catch (error: any) {
        addMessage(
          "connection",
          `Disconnect error: ${error.message}`,
          "error",
          false,
        );
      }
    }
  };

  const subscribeOrderbook = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "orderbook",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!marketId) {
      addMessage("orderbook", "Please select a market first", "error", false);
      return;
    }

    const callback = (data: any) => {
      addMessage("orderbook", data, "success", false);
    };

    try {
      subscriptionClientRef.current.subscribeOrderbook(
        marketId,
        callback,
        true,
      );
      setActiveSubscriptions((prev) => new Set([...prev, "orderbook"]));
      addMessage(
        "orderbook",
        `Subscribed to orderbook for market ${marketId}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "orderbook",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeOrderbook = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!marketId) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeOrderbook(marketId);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("orderbook");
        return next;
      });
      addMessage("orderbook", "Unsubscribed from orderbook", "info", false);
    } catch (error: any) {
      addMessage(
        "orderbook",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const subscribeTrades = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "trades",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!marketId) {
      addMessage("trades", "Please select a market first", "error", false);
      return;
    }

    const callback = (data: any) => {
      addMessage("trades", data, "success", false);
    };

    try {
      subscriptionClientRef.current.subscribeTrades(marketId, callback);
      setActiveSubscriptions((prev) => new Set([...prev, "trades"]));
      addMessage(
        "trades",
        `Subscribed to trades for market ${marketId}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "trades",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeTrades = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!marketId) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeTrades(marketId);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("trades");
        return next;
      });
      addMessage("trades", "Unsubscribed from trades", "info", false);
    } catch (error: any) {
      addMessage(
        "trades",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const subscribeTicker = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "ticker",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!marketId) {
      addMessage("ticker", "Please select a market first", "error", false);
      return;
    }

    const callback = (data: any) => {
      addMessage("ticker", data, "success", false);
    };

    try {
      subscriptionClientRef.current.subscribeTicker(marketId, callback);
      setActiveSubscriptions((prev) => new Set([...prev, "ticker"]));
      addMessage(
        "ticker",
        `Subscribed to ticker for market ${marketId}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "ticker",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeTicker = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!marketId) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeTicker(marketId);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("ticker");
        return next;
      });
      addMessage("ticker", "Unsubscribed from ticker", "info", false);
    } catch (error: any) {
      addMessage(
        "ticker",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const subscribeLastPrice = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "last_price",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!marketId) {
      addMessage("last_price", "Please select a market first", "error", false);
      return;
    }

    const callback = (data: any) => {
      addMessage("last_price", data, "success", false);
    };

    try {
      subscriptionClientRef.current.subscribeLastPrice(
        marketId,
        callback,
        true,
      );
      setActiveSubscriptions((prev) => new Set([...prev, "last_price"]));
      addMessage(
        "last_price",
        `Subscribed to last price for market ${marketId}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "last_price",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeLastPrice = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!marketId) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeLastPrice(marketId);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("last_price");
        return next;
      });
      addMessage("last_price", "Unsubscribed from last price", "info", false);
    } catch (error: any) {
      addMessage(
        "last_price",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const subscribeUserUpdates = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "user_updates",
        "Not connected. Please connect first.",
        "error",
        true,
      );
      return;
    }
    if (!walletAddress) {
      addMessage(
        "user_updates",
        "Wallet address required for position updates (positions channel)",
        "error",
        true,
      );
      return;
    }

    const callback = (data: any) => {
      addMessage("user_updates", data, "success", true);
    };

    try {
      subscriptionClientRef.current.subscribeUserUpdates(callback);
      setActiveSubscriptions((prev) => new Set([...prev, "user_updates"]));
      addMessage(
        "user_updates",
        "Subscribed to user updates (positions, orders, etc.)",
        "success",
        true,
      );
    } catch (error: any) {
      addMessage(
        "user_updates",
        `Subscription error: ${error.message}`,
        "error",
        true,
      );
    }
  };

  const unsubscribeUserUpdates = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeUserUpdates();
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("user_updates");
        return next;
      });
      addMessage(
        "user_updates",
        "Unsubscribed from user updates",
        "info",
        true,
      );
    } catch (error: any) {
      addMessage(
        "user_updates",
        `Unsubscribe error: ${error.message}`,
        "error",
        true,
      );
    }
  };

  const subscribeFundingRate = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "funding_rate",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!symbol) {
      addMessage("funding_rate", "Please enter a symbol first", "error", false);
      return;
    }

    const callback = (data: any) => {
      addMessage("funding_rate", data, "success", false);
    };

    try {
      subscriptionClientRef.current.subscribeFundingRate(symbol, callback);
      setActiveSubscriptions((prev) => new Set([...prev, "funding_rate"]));
      addMessage(
        "funding_rate",
        `Subscribed to funding rate for ${symbol}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "funding_rate",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeFundingRate = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!symbol) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeFundingRate(symbol);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("funding_rate");
        return next;
      });
      addMessage(
        "funding_rate",
        "Unsubscribed from funding rate",
        "info",
        false,
      );
    } catch (error: any) {
      addMessage(
        "funding_rate",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  // ============================================================================
  // New Channel Subscriptions
  // ============================================================================

  const subscribeBuckets = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "buckets",
        "Not connected. Please connect first.",
        "error",
        true,
      );
      return;
    }
    if (!walletAddress) {
      addMessage(
        "buckets",
        "Wallet address required for buckets subscription",
        "error",
        true,
      );
      return;
    }

    const callback = (data: any) => {
      addMessage("buckets", data, "success", true);
    };

    try {
      subscriptionClientRef.current.subscribeBuckets(callback);
      setActiveSubscriptions((prev) => new Set([...prev, "buckets"]));
      addMessage("buckets", "Subscribed to buckets", "success", true);
    } catch (error: any) {
      addMessage(
        "buckets",
        `Subscription error: ${error.message}`,
        "error",
        true,
      );
    }
  };

  const unsubscribeBuckets = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeBuckets();
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("buckets");
        return next;
      });
      addMessage("buckets", "Unsubscribed from buckets", "info", true);
    } catch (error: any) {
      addMessage(
        "buckets",
        `Unsubscribe error: ${error.message}`,
        "error",
        true,
      );
    }
  };

  const subscribeDeviation = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "deviation",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!symbol) {
      addMessage("deviation", "Please enter a symbol first", "error", false);
      return;
    }

    const callback = (data: any) => {
      addMessage("deviation", data, "success", false);
    };

    try {
      subscriptionClientRef.current.subscribeDeviation(symbol, callback);
      setActiveSubscriptions((prev) => new Set([...prev, "deviation"]));
      addMessage(
        "deviation",
        `Subscribed to deviation for ${symbol}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "deviation",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeDeviation = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!symbol) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeDeviation(symbol);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("deviation");
        return next;
      });
      addMessage("deviation", "Unsubscribed from deviation", "info", false);
    } catch (error: any) {
      addMessage(
        "deviation",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const subscribeIndexPrice = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "index_price",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!symbol) {
      addMessage("index_price", "Please enter a symbol first", "error", false);
      return;
    }

    const callback = (data: any) => {
      addMessage("index_price", data, "success", false);
    };

    try {
      subscriptionClientRef.current.subscribeIndexPrice(symbol, callback);
      setActiveSubscriptions((prev) => new Set([...prev, "index_price"]));
      addMessage(
        "index_price",
        `Subscribed to index price for ${symbol}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "index_price",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeIndexPrice = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!symbol) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeIndexPrice(symbol);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("index_price");
        return next;
      });
      addMessage("index_price", "Unsubscribed from index price", "info", false);
    } catch (error: any) {
      addMessage(
        "index_price",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const subscribeKline = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "kline",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!marketId) {
      addMessage("kline", "Please select a market first", "error", false);
      return;
    }
    if (!klineInterval) {
      addMessage("kline", "Please select an interval first", "error", false);
      return;
    }

    const callback = (data: any) => {
      addMessage("kline", data, "success", false);
      
      // Update chart with new kline data
      if (klineChartRef.current && data) {
        const klineUpdate: KlineUpdate = {
          open_time: data.open_time || data.timestamp || Date.now(),
          close_time: data.close_time,
          open: data.open,
          high: data.high,
          low: data.low,
          close: data.close,
          volume: data.volume,
        };
        klineChartRef.current.updateChart(klineUpdate);
        
        // Also update the bars array for initial data
        const time = Math.floor((klineUpdate.open_time || Date.now()) / 1000) as UTCTimestamp;
        const newBar: CandlestickData = {
          time,
          open: typeof klineUpdate.open === "string" ? fromSatoshi(klineUpdate.open) : klineUpdate.open,
          high: typeof klineUpdate.high === "string" ? fromSatoshi(klineUpdate.high) : klineUpdate.high,
          low: typeof klineUpdate.low === "string" ? fromSatoshi(klineUpdate.low) : klineUpdate.low,
          close: typeof klineUpdate.close === "string" ? fromSatoshi(klineUpdate.close) : klineUpdate.close,
        };
        
        setKlineBars((prev) => {
          const updated = [...prev];
          // If it's a new bar, push it; if updating current bar, replace last
          if (updated.length === 0 || updated[updated.length - 1].time !== newBar.time) {
            updated.push(newBar);
          } else {
            updated[updated.length - 1] = newBar;
          }
          return updated;
        });
      }
    };

    try {
      subscriptionClientRef.current.subscribeKline(
        marketId,
        klineInterval,
        callback,
      );
      setActiveSubscriptions((prev) => new Set([...prev, "kline"]));
      addMessage(
        "kline",
        `Subscribed to kline for market ${marketId} with interval ${klineInterval}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "kline",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeKline = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!marketId || !klineInterval) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeKline(marketId, klineInterval);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("kline");
        return next;
      });
      // Clear chart data when unsubscribing
      setKlineBars([]);
      addMessage("kline", "Unsubscribed from kline", "info", false);
    } catch (error: any) {
      addMessage(
        "kline",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const subscribeMarkPrice = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "mark_price",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!symbol) {
      addMessage("mark_price", "Please enter a symbol first", "error", false);
      return;
    }

    const callback = (data: any) => {
      addMessage("mark_price", data, "success", false);
    };

    try {
      subscriptionClientRef.current.subscribeMarkPrice(symbol, callback);
      setActiveSubscriptions((prev) => new Set([...prev, "mark_price"]));
      addMessage(
        "mark_price",
        `Subscribed to mark price for ${symbol}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "mark_price",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeMarkPrice = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!symbol) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeMarkPrice(symbol);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("mark_price");
        return next;
      });
      addMessage("mark_price", "Unsubscribed from mark price", "info", false);
    } catch (error: any) {
      addMessage(
        "mark_price",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const subscribePriceFeed = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "price_feed",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!symbol) {
      addMessage("price_feed", "Please enter a symbol first", "error", false);
      return;
    }

    const callback = (data: any) => {
      addMessage("price_feed", data, "success", false);
    };

    try {
      subscriptionClientRef.current.subscribePriceFeed(symbol, callback);
      setActiveSubscriptions((prev) => new Set([...prev, "price_feed"]));
      addMessage(
        "price_feed",
        `Subscribed to price feed for ${symbol}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "price_feed",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribePriceFeed = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!symbol) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribePriceFeed(symbol);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("price_feed");
        return next;
      });
      addMessage("price_feed", "Unsubscribed from price feed", "info", false);
    } catch (error: any) {
      addMessage(
        "price_feed",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const subscribeTWAP = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      addMessage(
        "twap",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!symbol) {
      addMessage("twap", "Please enter a symbol first", "error", false);
      return;
    }
    if (
      !twapWindowSize ||
      isNaN(Number(twapWindowSize)) ||
      Number(twapWindowSize) <= 0
    ) {
      addMessage(
        "twap",
        "Please enter a valid window size (positive number in seconds)",
        "error",
        false,
      );
      return;
    }

    const callback = (data: any) => {
      addMessage("twap", data, "success", false);
    };

    try {
      subscriptionClientRef.current.subscribeTWAP(
        symbol,
        Number(twapWindowSize),
        callback,
      );
      setActiveSubscriptions((prev) => new Set([...prev, "twap"]));
      addMessage(
        "twap",
        `Subscribed to TWAP for ${symbol} with window size ${twapWindowSize}s`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "twap",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeTWAP = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!symbol || !twapWindowSize) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeTWAP(
        symbol,
        Number(twapWindowSize),
      );
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("twap");
        return next;
      });
      addMessage("twap", "Unsubscribed from TWAP", "info", false);
    } catch (error: any) {
      addMessage("twap", `Unsubscribe error: ${error.message}`, "error", false);
    }
  };

  // ============================================================================
  // Multi-Feed and New Authenticated Channels
  // ============================================================================

  const subscribeMultiFeed = () => {
    if (!subscriptionClientRef.current) {
      addMessage(
        "multi_feed",
        "SubscriptionClient not initialized. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!subscriptionClientRef.current.isConnected()) {
      addMessage(
        "multi_feed",
        "Not connected. Please connect first.",
        "error",
        false,
      );
      return;
    }
    if (!marketId) {
      addMessage("multi_feed", "Please select a market first", "error", false);
      return;
    }
    if (!symbol) {
      addMessage("multi_feed", "Please enter a symbol first", "error", false);
      return;
    }

    // Verify method exists
    if (typeof subscriptionClientRef.current.subscribeMultiFeed !== 'function') {
      addMessage(
        "multi_feed",
        "subscribeMultiFeed method not available. Please check SDK version.",
        "error",
        false,
      );
      console.error('SubscriptionClient methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(subscriptionClientRef.current)));
      return;
    }

    const callback = (data: any) => {
      // Multi-feed messages include sub_channel info
      const subChannel = data.sub_channel || "unknown";
      addMessage(
        `multi_feed:${subChannel}`,
        data,
        "success",
        subChannel === "balances" || subChannel === "buckets" || subChannel === "orders" || subChannel === "positions",
      );
    };

    try {
      subscriptionClientRef.current.subscribeMultiFeed(
        marketId,
        symbol,
        callback,
        klineInterval || "1m",
      );
      setActiveSubscriptions((prev) => new Set([...prev, "multi_feed"]));
      addMessage(
        "multi_feed",
        `Subscribed to multi-feed for market ${marketId}, symbol ${symbol}`,
        "success",
        false,
      );
    } catch (error: any) {
      addMessage(
        "multi_feed",
        `Subscription error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const unsubscribeMultiFeed = () => {
    if (!subscriptionClientRef.current?.isConnected()) {
      return;
    }
    if (!marketId || !symbol) {
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeMultiFeed(marketId, symbol);
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("multi_feed");
        return next;
      });
      addMessage("multi_feed", "Unsubscribed from multi-feed", "info", false);
    } catch (error: any) {
      addMessage(
        "multi_feed",
        `Unsubscribe error: ${error.message}`,
        "error",
        false,
      );
    }
  };

  const subscribeBalances = () => {
    if (!subscriptionClientRef.current) {
      addMessage(
        "balances",
        "SubscriptionClient not initialized. Please connect first.",
        "error",
        true,
      );
      return;
    }
    if (!subscriptionClientRef.current.isConnected()) {
      addMessage(
        "balances",
        "Not connected. Please connect first.",
        "error",
        true,
      );
      return;
    }
    if (!walletAddress) {
      addMessage(
        "balances",
        "Wallet address required for balances subscription",
        "error",
        true,
      );
      return;
    }

    // Verify method exists
    if (typeof subscriptionClientRef.current.subscribeBalances !== 'function') {
      addMessage(
        "balances",
        "subscribeBalances method not available. Please check SDK version.",
        "error",
        true,
      );
      console.error('SubscriptionClient methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(subscriptionClientRef.current)));
      return;
    }

    const callback = (data: any) => {
      addMessage("balances", data, "success", true);
    };

    try {
      subscriptionClientRef.current.subscribeBalances(callback);
      setActiveSubscriptions((prev) => new Set([...prev, "balances"]));
      addMessage("balances", "Subscribed to balances", "success", true);
    } catch (error: any) {
      addMessage(
        "balances",
        `Subscription error: ${error.message}`,
        "error",
        true,
      );
    }
  };

  const unsubscribeBalances = () => {
    if (!subscriptionClientRef.current) {
      return;
    }
    if (!subscriptionClientRef.current.isConnected()) {
      return;
    }

    // Verify method exists
    if (typeof subscriptionClientRef.current.unsubscribeBalances !== 'function') {
      addMessage(
        "balances",
        "unsubscribeBalances method not available. Please check SDK version.",
        "error",
        true,
      );
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeBalances();
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("balances");
        return next;
      });
      addMessage("balances", "Unsubscribed from balances", "info", true);
    } catch (error: any) {
      addMessage(
        "balances",
        `Unsubscribe error: ${error.message}`,
        "error",
        true,
      );
    }
  };

  const subscribeOrders = () => {
    if (!subscriptionClientRef.current) {
      addMessage(
        "orders",
        "SubscriptionClient not initialized. Please connect first.",
        "error",
        true,
      );
      return;
    }
    if (!subscriptionClientRef.current.isConnected()) {
      addMessage(
        "orders",
        "Not connected. Please connect first.",
        "error",
        true,
      );
      return;
    }
    if (!walletAddress) {
      addMessage(
        "orders",
        "Wallet address required for orders subscription",
        "error",
        true,
      );
      return;
    }

    // Verify method exists
    if (typeof subscriptionClientRef.current.subscribeOrders !== 'function') {
      addMessage(
        "orders",
        "subscribeOrders method not available. Please check SDK version.",
        "error",
        true,
      );
      console.error('SubscriptionClient methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(subscriptionClientRef.current)));
      return;
    }

    const callback = (data: any) => {
      addMessage("orders", data, "success", true);
    };

    try {
      subscriptionClientRef.current.subscribeOrders(callback);
      setActiveSubscriptions((prev) => new Set([...prev, "orders"]));
      addMessage("orders", "Subscribed to orders", "success", true);
    } catch (error: any) {
      addMessage(
        "orders",
        `Subscription error: ${error.message}`,
        "error",
        true,
      );
    }
  };

  const unsubscribeOrders = () => {
    if (!subscriptionClientRef.current) {
      return;
    }
    if (!subscriptionClientRef.current.isConnected()) {
      return;
    }

    // Verify method exists
    if (typeof subscriptionClientRef.current.unsubscribeOrders !== 'function') {
      addMessage(
        "orders",
        "unsubscribeOrders method not available. Please check SDK version.",
        "error",
        true,
      );
      return;
    }

    try {
      subscriptionClientRef.current.unsubscribeOrders();
      setActiveSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete("orders");
        return next;
      });
      addMessage("orders", "Unsubscribed from orders", "info", true);
    } catch (error: any) {
      addMessage(
        "orders",
        `Unsubscribe error: ${error.message}`,
        "error",
        true,
      );
    }
  };

  const clearMessages = (isPrivate: boolean) => {
    if (isPrivate) {
      setPrivateMessages([]);
    } else {
      setPublicMessages([]);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (subscriptionClientRef.current) {
        try {
          subscriptionClientRef.current.disconnect();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  return (
    <div className="ws-test-page">
      <h1>WebSocket Test Page</h1>
      <p>Test WebSocket subscriptions for public and private channels</p>

      {/* Connection Steps Status */}
      <ConnectionSteps steps={connectionSteps} />

      {/* Configuration */}
      <ConfigSection
        wsURL={wsURL}
        onWsURLChange={setWsURL}
        marketId={marketId}
        onMarketIdChange={setMarketId}
        markets={markets}
        symbol={symbol}
        onSymbolChange={setSymbol}
        klineInterval={klineInterval}
        onKlineIntervalChange={setKlineInterval}
        twapWindowSize={twapWindowSize}
        onTwapWindowSizeChange={setTwapWindowSize}
        authToken={authToken}
        onAuthTokenChange={setAuthToken}
        walletAddress={walletAddress}
        deviceID={deviceID}
        onDeviceIDChange={setDeviceID}
        isConnected={isConnected}
        isConnecting={isConnecting}
        onConnect={connect}
        onDisconnect={disconnect}
      />

      {/* Two Column Layout: Public and Private */}
      <div className="ws-test-page-layout">
        {/* Public Channels Section */}
        <ChannelSection
          title="WebSocket Public"
          isPrivate={false}
          messages={publicMessages}
          onClearMessages={() => clearMessages(false)}
        >
          <div className="channel-buttons-row">
                <SubscriptionButton
                  channel="orderbook"
                  isSubscribed={activeSubscriptions.has("orderbook")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Orderbook"
                  unsubscribeLabel="Unsubscribe Orderbook"
                  onSubscribe={subscribeOrderbook}
                  onUnsubscribe={unsubscribeOrderbook}
                />
                <SubscriptionButton
                  channel="trades"
                  isSubscribed={activeSubscriptions.has("trades")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Trades"
                  unsubscribeLabel="Unsubscribe Trades"
                  onSubscribe={subscribeTrades}
                  onUnsubscribe={unsubscribeTrades}
                />
                <SubscriptionButton
                  channel="ticker"
                  isSubscribed={activeSubscriptions.has("ticker")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Ticker"
                  unsubscribeLabel="Unsubscribe Ticker"
                  onSubscribe={subscribeTicker}
                  onUnsubscribe={unsubscribeTicker}
                />
                <SubscriptionButton
                  channel="last_price"
                  isSubscribed={activeSubscriptions.has("last_price")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Last Price"
                  unsubscribeLabel="Unsubscribe Last Price"
                  onSubscribe={subscribeLastPrice}
                  onUnsubscribe={unsubscribeLastPrice}
                />
                <SubscriptionButton
                  channel="funding_rate"
                  isSubscribed={activeSubscriptions.has("funding_rate")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Funding Rate"
                  unsubscribeLabel="Unsubscribe Funding Rate"
                  onSubscribe={subscribeFundingRate}
                  onUnsubscribe={unsubscribeFundingRate}
                />
                <SubscriptionButton
                  channel="deviation"
                  isSubscribed={activeSubscriptions.has("deviation")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Deviation"
                  unsubscribeLabel="Unsubscribe Deviation"
                  onSubscribe={subscribeDeviation}
                  onUnsubscribe={unsubscribeDeviation}
                />
                <SubscriptionButton
                  channel="index_price"
                  isSubscribed={activeSubscriptions.has("index_price")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Index Price"
                  unsubscribeLabel="Unsubscribe Index Price"
                  onSubscribe={subscribeIndexPrice}
                  onUnsubscribe={unsubscribeIndexPrice}
                />
                <SubscriptionButton
                  channel="kline"
                  isSubscribed={activeSubscriptions.has("kline")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Kline"
                  unsubscribeLabel="Unsubscribe Kline"
                  onSubscribe={subscribeKline}
                  onUnsubscribe={unsubscribeKline}
                />
          </div>
          {/* Kline Chart - Show when subscribed */}
          {activeSubscriptions.has("kline") && (
            <div style={{ marginTop: "20px", marginBottom: "20px" }}>
              <KlineChart
                ref={klineChartRef}
                initialData={klineBars}
                symbol={symbol || "BTC/USDC"}
                height={500}
                showVolume={true}
              />
            </div>
          )}
          <div className="channel-buttons-row">
                <SubscriptionButton
                  channel="mark_price"
                  isSubscribed={activeSubscriptions.has("mark_price")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Mark Price"
                  unsubscribeLabel="Unsubscribe Mark Price"
                  onSubscribe={subscribeMarkPrice}
                  onUnsubscribe={unsubscribeMarkPrice}
                />
                <SubscriptionButton
                  channel="price_feed"
                  isSubscribed={activeSubscriptions.has("price_feed")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Price Feed"
                  unsubscribeLabel="Unsubscribe Price Feed"
                  onSubscribe={subscribePriceFeed}
                  onUnsubscribe={unsubscribePriceFeed}
                />
                <SubscriptionButton
                  channel="twap"
                  isSubscribed={activeSubscriptions.has("twap")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe TWAP"
                  unsubscribeLabel="Unsubscribe TWAP"
                  onSubscribe={subscribeTWAP}
                  onUnsubscribe={unsubscribeTWAP}
                />
                <SubscriptionButton
                  channel="multi_feed"
                  isSubscribed={activeSubscriptions.has("multi_feed")}
                  isConnected={isConnected}
                  subscribeLabel="Subscribe Multi-Feed"
                  unsubscribeLabel="Unsubscribe Multi-Feed"
                  onSubscribe={subscribeMultiFeed}
                  onUnsubscribe={unsubscribeMultiFeed}
                />
          </div>
          <small className="private-note-text-block" style={{ marginTop: "10px" }}>
            Multi-Feed: Unified channel streaming all sub-channels (orderbook, trades, positions, balances, orders, buckets, etc.)
            Requires market ID and symbol. Authenticated sub-channels (balances, orders, positions, buckets) require wallet address.
          </small>
        </ChannelSection>

        {/* Private Channels Section */}
        <ChannelSection
          title="WebSocket Private"
          isPrivate={true}
          messages={privateMessages}
          onClearMessages={() => clearMessages(true)}
        >
          <SubscriptionButton
            channel="user_updates"
            isSubscribed={activeSubscriptions.has("user_updates")}
            isConnected={isConnected}
            disabled={!walletAddress}
            subscribeLabel="Subscribe User Updates"
            unsubscribeLabel="Unsubscribe User Updates"
            onSubscribe={subscribeUserUpdates}
            onUnsubscribe={unsubscribeUserUpdates}
            variant="private"
          />
          <small className="private-note-text">
            Position updates (positions channel): Requires wallet address.
            Returns user positions.
          </small>
          <SubscriptionButton
            channel="buckets"
            isSubscribed={activeSubscriptions.has("buckets")}
            isConnected={isConnected}
            disabled={!walletAddress}
            subscribeLabel="Subscribe Buckets"
            unsubscribeLabel="Unsubscribe Buckets"
            onSubscribe={subscribeBuckets}
            onUnsubscribe={unsubscribeBuckets}
            variant="private"
          />
          <small className="private-note-text-block">
            Buckets: User's margin buckets (requires wallet address)
          </small>
          <SubscriptionButton
            channel="balances"
            isSubscribed={activeSubscriptions.has("balances")}
            isConnected={isConnected}
            disabled={!walletAddress}
            subscribeLabel="Subscribe Balances"
            unsubscribeLabel="Unsubscribe Balances"
            onSubscribe={subscribeBalances}
            onUnsubscribe={unsubscribeBalances}
            variant="private"
          />
          <small className="private-note-text-block">
            Balances: User's spot balances (USDC, MORM, BTC, etc.) - requires wallet address
          </small>
          <SubscriptionButton
            channel="orders"
            isSubscribed={activeSubscriptions.has("orders")}
            isConnected={isConnected}
            disabled={!walletAddress}
            subscribeLabel="Subscribe Orders"
            unsubscribeLabel="Unsubscribe Orders"
            onSubscribe={subscribeOrders}
            onUnsubscribe={unsubscribeOrders}
            variant="private"
          />
          <small className="private-note-text-block">
            Orders: User's open orders - requires wallet address
          </small>
        </ChannelSection>
      </div>
    </div>
  );
}
